import React from 'react';
import {
  AMHS_LOAD_PORT_SHADING,
  AMHS_PALETTE,
  STATUS_LED_COLOR,
  type EquipmentStatus,
} from '../types';
import { FAB_COLORS } from '../rendering/fabColors';

export type PortShutterProps = {
  /** 0 = fully closed, 1 = fully open (raised). */
  open: number;
  width: number;
  height: number;
  depth?: number;
  /** Vertical travel when open=1. */
  openTravel: number;
  opacity?: number;
  /**
   * Status shown in the shutter face panel:
   * RUNNING = teal accent · IDLE = pale blue · OFFLINE = deep violet.
   */
  status?: EquipmentStatus;
};

/** Unlit panel colors (toneMapped off so hex stays exact). */
const SHUTTER_BULB_COLOR: Record<EquipmentStatus, string> = STATUS_LED_COLOR;

/**
 * Per–load-port EFEM shutter — slides up when opening.
 * Front panel is an unlit status bulb so red/green stay fully saturated.
 */
export const PortShutter: React.FC<PortShutterProps> = ({
  open,
  width,
  height,
  depth = 0.045,
  openTravel,
  opacity = 1,
  status = 'IDLE',
}) => {
  const t = Math.min(1, Math.max(0, open));
  const yLift = t * openTravel;
  const a = opacity < 1;
  const led = SHUTTER_BULB_COLOR[status];

  const panelW = Math.min(width * 0.38, height * 0.7);
  const panelH = Math.max(0.02, height * 0.04);
  const glassW = width * 0.96;
  const glassH = height * 0.94;

  return (
    <group name="PortShutter" position={[0, yLift, 0]}>
      {/* Frameless dark baby pink glass — replaces wire mesh, slats, and black frame. */}
      <mesh castShadow position={[0, 0, depth * 0.28]}>
        <planeGeometry args={[glassW, glassH]} />
        <meshStandardMaterial
          color={FAB_COLORS.babyPinkGlassDeep}
          metalness={0.06}
          roughness={0.1}
          envMapIntensity={AMHS_LOAD_PORT_SHADING.envMapIntensity}
          transparent
          opacity={Math.min(0.88, opacity * 0.86)}
          depthWrite={false}
        />
      </mesh>

      {/* Status bulb below the glass — unlit so the accent hex stays exact */}
      <group position={[0, -(glassH * 0.5 + panelH * 0.6), depth * 0.55]} name="ShutterStatusLed">
        <mesh position={[0, 0, -depth * 0.05]}>
          <boxGeometry args={[panelW + panelH * 0.4, panelH + panelH * 0.4, depth * 0.2]} />
          <meshStandardMaterial
            color={AMHS_PALETTE.metalDark}
            metalness={AMHS_LOAD_PORT_SHADING.metalness}
            roughness={AMHS_LOAD_PORT_SHADING.roughness}
            envMapIntensity={AMHS_LOAD_PORT_SHADING.envMapIntensity}
            transparent={a}
            opacity={opacity}
          />
        </mesh>
        <mesh>
          <boxGeometry args={[panelW, panelH, depth * 0.4]} />
          <meshBasicMaterial color={led} toneMapped={false} />
        </mesh>
      </group>
    </group>
  );
};

export default PortShutter;
